/**
 * The effect layer: a transparent WebGL sheet over the board, and the thing that
 * turns a set of authoritative cells into something that looks like it happened.
 *
 * It is deliberately shaped like PedestalStage, its neighbour in services/model3d —
 * one renderer, one scene, geometry authored in the board's own coordinates, and no
 * camera of its own. It draws effects and nothing else: no targeting, no cells of
 * its own invention, no opinion about which skill is being cast.
 *
 * Per connected component it builds exactly one merged ground decal, one particle
 * domain and at most one primary volume. That budget is the whole design: it is
 * what stops a thirteen-cell burst rendering as thirteen explosions.
 *
 *   authoritative cells
 *     -> connected components          topology.js
 *     -> merged footprint geometry     this file
 *     -> ImpactViolationType material  materials.js
 *     -> surface, volume, particles, light
 */

import {
  BufferAttribute,
  BufferGeometry,
  Camera,
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DataTexture,
  ExtrudeGeometry,
  Group,
  LinearFilter,
  Mesh,
  PlaneGeometry,
  Points,
  RGBAFormat,
  Scene,
  Shape,
  SphereGeometry,
  Vector2,
  WebGLRenderer,
} from 'three';

import { WORLD_PROJECTION } from './shaders';
import { buildTopology, seededRandom } from './topology';
import { domainSampler, footprintParams } from './footprints';
import {
  Trace, advanceSlash, createArrival, createConverge, createSlash,
} from './traces';
import impactShake from './shake';
import {
  ambientMotion,
  burstStyle,
  createAmbientMaterial,
  createBurstMaterial,
  createDecalMaterial,
  createFlashMaterial,
  createOcclusionMaterial,
  createShardMaterial,
  createSigilMaterial,
  createTrailMaterial,
  createVolumeMaterial,
  trailStyle,
} from './materials';
import { INTENTS } from './presentation';
import {
  PHASE_DURATION_MS,
  QUALITY_PROFILES,
  normalizeViolationType,
  violationProfile,
} from './violation';

/**
 * How much of itself an aim shows.
 *
 * The preview used to sit at 0.3 activation behind a contour field that was doing all
 * the talking, so it barely had to be there. It is now the aim's LEAD statement — the
 * skill's own material laid on the ground it would cover — and at 0.3 the tint lost
 * to the map texture under it on anything but the darkest board.
 *
 * The ceiling is set by what a player can hold on screen without wanting it gone.
 * Above about 0.6 a Heat aim starts to read as the fireball having already landed,
 * which is a lie about the game state and takes the moment away from the cast that
 * follows it.
 */
const AIM_ACTIVATION = 0.52;

/**
 * How far the surface has grown while it is only being aimed.
 *
 * Growth is what carries the effect out to its own boundary, so an aim held at 1.0
 * would be indistinguishable from the thing having happened. Held at a third: enough
 * for the material to reach the cells it claims, short of the reach it will take when
 * it lands.
 */
const AIM_GROWTH = 0.34;

/**
 * How far the aim is sunk toward its own unlit colour.
 *
 * Not a dimmer. See `shadowed` in shaders.js: at 0.6 the material is unmistakably the
 * one that will land — a Heat aim is deep orange-red, a Cold aim deep blue — with the
 * white-hot centre that says "this is happening" taken out of it.
 */
const AIM_SHADOW = 0.6;

const RENDER_ORDER = {
  occlusion: 0,
  decal: 1,
  sigil: 2,
  shards: 3,
  volume: 4,
  flash: 5,
  particles: 6,
  burst: 7,
};

function sdfTexture(sdf) {
  const texture = new DataTexture(sdf.data, sdf.width, sdf.height, RGBAFormat);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.flipY = false;
  texture.needsUpdate = true;
  return texture;
}

/** How far the effect reaches, when the caller gave cells but no size. */
function deriveSize(cells, origin) {
  let reach = 1;
  cells.forEach(cell => {
    const distance = Math.hypot(cell.col - origin.col, cell.row - origin.row);
    if (distance > reach) reach = distance;
  });
  return Math.max(1, Math.round(reach));
}

/** The cell bounds grown to hold the ideal shape as well. */
function idealBounds(rect, params) {
  const reach = params.reach + 0.6;
  const minX = Math.min(rect.x, params.origin.x - reach);
  const maxX = Math.max(rect.x + rect.width, params.origin.x + reach);
  const minZ = Math.min(rect.z, params.origin.z - reach);
  const maxZ = Math.max(rect.z + rect.height, params.origin.z + reach);
  return { x: minX, z: minZ, width: maxX - minX, height: maxZ - minZ };
}

function profileColorOf(violation, path) {
  return violationProfile(violation, path).color;
}

function directionDegreesOf(instance) {
  const direction = instance.direction;
  if (!direction) return 0;
  const z = direction.z ?? direction.y ?? 0;
  return (Math.atan2(z, direction.x || 0) * 180) / Math.PI;
}

/**
 * One effect instance: its topology, its materials, its lifecycle clock.
 *
 * Intensity is normalized by area before it reaches any material. Doubling the
 * number of affected cells must not double the brightness — a wide field is wider,
 * not brighter, and without this a big burst blows the board out while a small one
 * barely registers.
 */
class ImpactEffect {
  constructor(instance, { projectionUniforms, quality }) {
    this.instance = instance;
    this.violation = normalizeViolationType(instance.violationType);
    this.path = instance.path || null;
    this.profile = violationProfile(this.violation, this.path);
    this.intent = INTENTS[instance.intent] || INTENTS.harm;
    this.quality = QUALITY_PROFILES[quality] || QUALITY_PROFILES.high;
    this.group = new Group();
    this.textures = [];
    this.componentTextures = new Map();
    this.phase = instance.phase || 'preview';
    this.phaseElapsed = 0;

    this.topology = buildTopology(instance.cells || [], {
      revision: instance.gridRevision || 0,
      quality,
      pixelsPerCell: this.quality.sdfPixelsPerCell,
      /*
       * `rounded-cells` is the fight's mode and it exists to stop a footprint reading
       * as a spreadsheet. It was doing that downstream, by blurring an exact union of
       * squares — which cannot remove a right angle, only soften it. Building the
       * field itself out of discs removes it, so the mode finally means what it says.
       */
      radial: (instance.mergeMode || 'clipped-shape') === 'rounded-cells' || instance.aim === true,
    });
    const cells = this.topology.components.flatMap(component => component.cells);
    const rawOrigin = instance.origin || cells[0] || { col: 0, row: 0 };
    const origin = { col: rawOrigin.col ?? rawOrigin.x ?? 0, row: rawOrigin.row ?? rawOrigin.y ?? 0 };
    this.params = footprintParams(instance.footprintHint, {
      origin,
      size: instance.size || deriveSize(cells, origin),
      directionDegrees: directionDegreesOf(instance),
      cells,
    });
    this.groundReach = cells.reduce((reach, cell) => Math.max(
      reach,
      Math.hypot(cell.col + 0.5 - this.params.origin.x, cell.row + 0.5 - this.params.origin.z) + 0.5,
    ), 0.8);

    const area = Math.max(1, this.topology.area);
    this.uniforms = {
      ...projectionUniforms,
      uTime: { value: 0 },
      uActivation: { value: 0.3 },
      uBurst: { value: 0 },
      uDissolve: { value: 0 },
      uGrowth: { value: 0.25 },
      uChar: { value: 0 },
      uIntensity: { value: instance.intensity ?? 0.8 },
      /* An aim is the effect in shadow; anything that is actually happening is not. */
      uShadow: { value: instance.aim === true ? (instance.shadow ?? AIM_SHADOW) : 0 },
      /*
       * Seconds since the strike, on its own clock.
       *
       * The burst, the ground flash and the impact bloom are all one-shot events
       * with real durations, and `uBurst` — which saturates at one and then stays
       * there for the rest of the effect's life — cannot express any of them. This
       * runs from zero at the moment of arrival and never resets.
       */
      uSince: { value: 0 },
      uAreaNorm: { value: 1 / (0.55 + 0.45 * Math.sqrt(area / 6)) },
      uSeed: { value: ((instance.seed || 0) % 1000) * 0.137 },
      uOctaves: { value: this.quality.octaves },
      uIntent: { value: this.intent.index },
      uIntentBias: { value: new Color(this.intent.bias || profileColorOf(this.violation, this.path)) },
      uIntentAmount: { value: this.intent.biasAmount },
      uLift: { value: this.intent.lift },
      uConverge: { value: this.intent.converge },
    };

    /*
     * Everything that is the ARRIVAL rather than the aim. An aim is a statement about
     * ground; a flash and a body are statements about a moment. Held in preview and
     * shown from execute onward — the meshes are still built, because rebuilding the
     * effect between phases would re-rasterise the whole topology at the exact
     * instant the frame budget is tightest.
     */
    this.arrivalOnly = [];

    /* An aim, rather than something that is happening. Declared by the caller. */
    this.isAim = instance.aim === true;

    /*
     * How much of itself an aim shows. Not a dimmed cast: the surface and its motes
     * carry the material at a weight a player can hold on screen for ten seconds,
     * while everything one-shot stays out of it entirely.
     */
    this.aimActivation = instance.aimActivation ?? AIM_ACTIVATION;
    this.aimGrowth = instance.aimGrowth ?? AIM_GROWTH;

    this.build();
    this.applyPhaseVisibility();
  }

  /** The arrival's own pieces are absent from an aim, not merely faint. */
  applyPhaseVisibility() {
    const arriving = !this.isAim || this.phase !== 'preview';
    this.arrivalOnly.forEach(mesh => { mesh.visible = arriving; });
  }

  build() {
    const { instance } = this;
    const hint = instance.footprintHint;
    const mergeMode = instance.mergeMode || 'clipped-shape';
    const random = seededRandom(instance.seed || 1);
    const totalArea = Math.max(1, this.topology.area);

    /*
     * A component too small to earn particles by area still gets a floor, or a single
     * outlying cell renders as bare ground. The floor is shared out rather than fixed:
     * at a flat 30 each, six disconnected hits ask for 180 particles on a tier whose
     * whole budget is 150. Capping it at an even split keeps the total inside the tier
     * however the blast fragments, with no frame-time feedback anywhere in it.
     */
    const floor = Math.min(30, Math.floor(this.quality.particles / Math.max(1, this.topology.components.length)));

    this.topology.components.forEach(component => {
      const texture = sdfTexture(component.sdf);
      this.textures.push(texture);
      this.componentTextures.set(component, texture);

      /*
       * The surface is drawn on a quad big enough to hold whatever the mask can
       * claim. In `ideal-shape` the clean shape is allowed to run past the cell
       * union — that is the whole point of the mode — so a quad cut to the cells
       * would slice the effect off along a straight line nothing in the scene
       * explains.
       */
      const rect = mergeMode === 'ideal-shape'
        ? idealBounds(component.sdf.rect, this.params)
        : component.sdf.rect;
      const segments = (span) => Math.max(2, Math.min(64, Math.ceil(span * 3)));
      const surface = new PlaneGeometry(rect.width, rect.height, segments(rect.width), segments(rect.height));
      surface.rotateX(-Math.PI / 2);
      surface.translate(rect.x + rect.width / 2, 0, rect.z + rect.height / 2);
      const decal = new Mesh(surface, createDecalMaterial({
        shared: this.uniforms,
        component,
        violation: this.violation,
        path: this.path,
        hint,
        mergeMode,
        params: this.params,
        sdfTexture: texture,
      }));
      decal.renderOrder = RENDER_ORDER.decal;
      decal.frustumCulled = false;
      this.group.add(decal);

      const sample = domainSampler(component, hint, this.params, mergeMode, random);
      const share = component.area / totalArea;
      /*
       * The budget is split between the two systems rather than spent twice. The
       * burst is what the player is looking at, so it takes the larger share; the
       * ambient loop is what is left over once the strike is over, and a thin one
       * reads as smouldering while a thick one reads as static noise.
       */
      const budget = Math.max(floor, Math.round(this.quality.particles * share));
      /*
       * Density is per CELL, not per effect. Spending the whole tier budget on a
       * one-cell hit packs three hundred additive sprites into a single square, and
       * additive sprites that overlap that hard stop being sparks and become one
       * white ball — which is exactly what a cheap effect looks like. The tier still
       * caps the total; this caps how much of it any one patch of ground may hold.
       */
      const density = Math.round(30 + component.area * 26);
      const ambientCount = Math.max(6, Math.min(Math.round(budget * 0.38), Math.round(density * 0.4)));
      const burstCount = Math.max(12, Math.min(budget - ambientCount, density));

      const positions = new Float32Array(ambientCount * 3);
      const randoms = new Float32Array(ambientCount * 4);
      for (let i = 0; i < ambientCount; i++) {
        const point = sample();
        positions[i * 3] = point.x;
        positions[i * 3 + 1] = 0.03 + random() * 0.05;
        positions[i * 3 + 2] = point.z;
        randoms[i * 4] = random();
        randoms[i * 4 + 1] = random();
        randoms[i * 4 + 2] = random();
        randoms[i * 4 + 3] = random();
      }
      const cloud = new BufferGeometry();
      cloud.setAttribute('position', new BufferAttribute(positions, 3));
      cloud.setAttribute('aRandom', new BufferAttribute(randoms, 4));
      const points = new Points(cloud, createAmbientMaterial({
        shared: this.uniforms,
        violation: this.violation,
        path: this.path,
        hint,
        params: this.params,
        motion: ambientMotion(this.violation),
        /*
         * Declared, not inferred from the phase. A cast is constructed in `preview`
         * for the one frame before it is told to execute, and inferring the aim from
         * that would build every cast's ambient cloud under the aim's law of motion
         * and then leave it there for the effect's whole life.
         */
        aim: this.isAim,
      }));
      points.renderOrder = RENDER_ORDER.particles;
      points.frustumCulled = false;
      points.userData.particleCount = ambientCount;
      this.group.add(points);

      this.addBurst(component, sample, random, burstCount);

      if (this.profile.crystals) this.addShards(component, sample, random, 'crystal');
      if (this.profile.rays) this.addShards(component, sample, random, 'ray');
    });

    this.addVolume(hint, mergeMode);
    if (this.intent.sigil) this.addSigil();
    this.addFlash();
    if (this.profile.halo) this.addHalo();
    if (this.isAim) this.addTrail();
  }

  /**
   * What the aim's firing line became.
   *
   * It was three stroked polylines with a shadowBlur under each and five circles
   * sliding along them — a neon tube, drawn in 2D, over the board rather than in it.
   * It said where the shot would go and nothing about what the shot was, and on a
   * raked board it sat visibly on the glass because a stroke has no height and the
   * board does.
   *
   * The replacement is the material in flight: a dense stream of particles leaving
   * the caster and running dead straight and level to the aim point, tight enough to
   * read as one beam with the element's grain in it rather than as a spray. It is in
   * the same 3D space as the ground tint it feeds, so it takes the board's rake and
   * morph for free, and it holds on a diagonal or an aim running along the rake for
   * the same reason — there is no screen-space line anywhere in it.
   */
  addTrail() {
    const caster = this.instance.caster;
    if (!caster) return;
    const from = {
      x: (caster.col ?? caster.x ?? 0) + 0.5,
      z: (caster.row ?? caster.y ?? caster.z ?? 0) + 0.5,
    };
    const to = { x: this.params.origin.x, z: this.params.origin.z };
    const span = Math.hypot(to.x - from.x, to.z - from.z);
    /* A self-cast has no gap to cross, and a stream of particles standing still on
       the caster's own cell is a mistake rather than an effect. */
    if (span < 0.8) return;

    const style = trailStyle(this.violation);
    /* Per cell of flight, so a long shot is a stream and a short one is not a crowd.
       Dense, because the run reads as a BEAM: too few and the same straight line is
       a dotted one, and a dotted line is a diagram again. */
    const count = Math.max(96, Math.min(420, Math.round(span * 66)));
    const random = seededRandom((this.instance.seed || 1) + 977);

    const positions = new Float32Array(count * 3);
    const trail = new Float32Array(count * 4);
    /*
     * Most of the stream is the sheath, not the beam. The beam is a thin fast line;
     * what sells it as something moving THROUGH the air is the slower, finer material
     * hanging around it, and that only reads if there is more of it than of the line.
     */
    const sheath = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      sheath[i] = i % 3 === 0 ? 0 : 1;
      /* The position attribute is unused by the trail's vertex shader — the path is
         solved from `uFrom`/`uTo` — but three.js needs one to size the draw. */
      positions[i * 3] = from.x;
      positions[i * 3 + 2] = from.z;
      trail[i * 4] = i / count;
      trail[i * 4 + 1] = random();
      trail[i * 4 + 2] = random();
      trail[i * 4 + 3] = random();
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('aTrail', new BufferAttribute(trail, 4));
    geometry.setAttribute('aSheath', new BufferAttribute(sheath, 1));

    const points = new Points(geometry, createTrailMaterial({
      shared: this.uniforms,
      violation: this.violation,
      path: this.path,
      from,
      to,
      style,
    }));
    points.renderOrder = RENDER_ORDER.burst;
    points.frustumCulled = false;
    points.userData.particleCount = count;
    this.group.add(points);
  }

  /**
   * The impact cloud: debris thrown by something, in the direction it was thrown.
   *
   * The velocity is the whole point. A scatter of particles sitting in the affected
   * cells is a texture; the same particles leaving the impact along the line the
   * attack came in on, decelerating and falling, is an event. So each one is given a
   * forward component inherited from the trace, a randomised component to open the
   * cone out, and a rise — and about a fifth of them are thrown BACK toward the
   * caster, because a real impact splashes both ways and a pure forward cone reads
   * as a sprayer.
   */
  addBurst(component, sample, random, count) {
    const style = burstStyle(this.violation);
    const origin = this.params.origin;
    const inward = Math.max(0, this.intent.converge);
    const outward = Math.max(0, -this.intent.converge);
    const direction = this.instance.direction || { x: 1, z: 0 };
    const length = Math.hypot(direction.x, direction.z || 0) || 1;
    const dirX = direction.x / length;
    const dirZ = (direction.z ?? 0) / length;

    const positions = new Float32Array(count * 3);
    const velocity = new Float32Array(count * 3);
    const burst = new Float32Array(count * 4);

    for (let i = 0; i < count; i++) {
      const point = sample();
      positions[i * 3] = point.x;
      positions[i * 3 + 1] = 0.04 + random() * 0.14;
      positions[i * 3 + 2] = point.z;

        const back = random() < 0.2 ? -0.45 : 1;
      const forward = style.speed * (0.35 + 0.65 * random()) * back * outward;
      const turn = random() * Math.PI * 2;
      const scatter = style.spread * random() * random() * (1 - inward * 0.7);
      /*
       * `INTENTS.converge` finally reaches the debris. Every impact used to throw
       * its particles OUT along the line it arrived on, which is right for a blow
       * and exactly backwards for a mend or a ward: a heal that scatters embers
       * across two cells of floor is drawn as an explosion in a friendly colour.
       * A converging intent inherits no forward throw at all and is pulled back
       * toward the subject instead.
       */
      const pull = inward * style.speed * 0.5 * (0.4 + 0.6 * random());
      const spread = Math.hypot(point.x - origin.x, point.z - origin.z) || 1;
      velocity[i * 3] = dirX * forward + Math.cos(turn) * scatter - ((point.x - origin.x) / spread) * pull;
      velocity[i * 3 + 1] = style.rise * (0.25 + 0.85 * random()) * (1 + Math.max(0, this.intent.lift) * 0.9);
      velocity[i * 3 + 2] = dirZ * forward + Math.sin(turn) * scatter - ((point.z - origin.z) / spread) * pull;

      /* A handful arrive late, so the cloud has a tail instead of one flat front. */
      burst[i * 4] = random() < 0.85 ? random() * 0.05 : 0.05 + random() * 0.22;
      burst[i * 4 + 1] = style.life[0] + (style.life[1] - style.life[0]) * random();
      burst[i * 4 + 2] = style.size[0] + (style.size[1] - style.size[0]) * random();
      burst[i * 4 + 3] = random();
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('aVelocity', new BufferAttribute(velocity, 3));
    geometry.setAttribute('aBurst', new BufferAttribute(burst, 4));

    const points = new Points(geometry, createBurstMaterial({
      shared: this.uniforms,
      violation: this.violation,
      path: this.path,
      /* What is given rises; what is done to someone falls. */
      gravity: style.gravity - Math.max(0, this.intent.lift) * (style.gravity + 1.2),
    }));
    points.renderOrder = RENDER_ORDER.burst;
    points.frustumCulled = false;
    points.userData.particleCount = count;
    this.group.add(points);
  }

  /**
   * Crystal growth and light rays, merged into one buffer.
   *
   * The shards are sampled across the whole merged domain rather than dropped at
   * cell centres, which is the difference between a frost front and a chequerboard.
   */
  addShards(component, sample, random, kind) {
    const count = kind === 'crystal'
      ? Math.min(72, 6 + Math.round(component.area * 2.6))
      : Math.min(7, 2 + Math.round(component.area * 0.28));
    const template = kind === 'crystal'
      ? new ConeGeometry(0.07, 0.44, 5)
      : new PlaneGeometry(0.5, 2.2);
    if (kind === 'crystal') template.translate(0, 0.22, 0);
    else template.translate(0, 1.0, 0);

    const templatePositions = template.getAttribute('position');
    const templateUv = template.getAttribute('uv');
    const templateIndex = template.getIndex();
    const vertices = templatePositions.count;
    const positions = new Float32Array(count * vertices * 3);
    const bases = new Float32Array(count * vertices * 3);
    const shard = new Float32Array(count * vertices * 4);
    const indices = [];

    for (let i = 0; i < count; i++) {
      const point = sample();
      const scale = kind === 'crystal' ? 0.5 + random() * 1.1 : 0.6 + random() * 0.9;
      const tilt = kind === 'crystal' ? (random() - 0.5) * 0.7 : 0;
      const turn = random() * Math.PI * 2;
      const phase = random();
      const delay = Math.min(1, Math.hypot(point.x - this.params.origin.x, point.z - this.params.origin.z) / Math.max(this.params.reach, 0.1));
      for (let v = 0; v < vertices; v++) {
        const lx = templatePositions.getX(v) * scale;
        const ly = templatePositions.getY(v) * scale;
        const lz = templatePositions.getZ(v) * scale;
        const cos = Math.cos(turn);
        const sin = Math.sin(turn);
        const rx = lx * cos - lz * sin;
        const rz = lx * sin + lz * cos;
        const leanX = rx + ly * tilt;
        const at = (i * vertices + v) * 3;
        positions[at] = point.x + leanX;
        positions[at + 1] = ly;
        positions[at + 2] = point.z + rz;
        bases[at] = point.x;
        bases[at + 1] = 0;
        bases[at + 2] = point.z;
        const at2 = (i * vertices + v) * 4;
        shard[at2] = delay;
        shard[at2 + 1] = phase;
        /* The template's own uv, so the material can fade a shard out at its edges
           instead of showing the card it is drawn on. */
        shard[at2 + 2] = templateUv ? templateUv.getX(v) : 0.5;
        shard[at2 + 3] = templateUv ? 1.0 - templateUv.getY(v) : 0.0;
      }
      if (templateIndex) {
        for (let k = 0; k < templateIndex.count; k++) {
          indices.push(templateIndex.getX(k) + i * vertices);
        }
      }
    }
    template.dispose();

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('aBase', new BufferAttribute(bases, 3));
    geometry.setAttribute('aShard', new BufferAttribute(shard, 4));
    if (indices.length) geometry.setIndex(indices);

    const mesh = new Mesh(geometry, createShardMaterial({
      shared: this.uniforms,
      violation: this.violation,
      path: this.path,
      kind,
    }));
    mesh.renderOrder = RENDER_ORDER.shards;
    mesh.frustumCulled = false;
    this.group.add(mesh);
  }

  /**
   * One primary volume, on the component the origin stands in.
   *
   * A line and an arbitrary cluster get none: there is no single body they are
   * shaped like, and the honest answer is a surface and a flow rather than a
   * plausible-looking blob invented to fill the space.
   */
  addVolume(hint, mergeMode) {
    /* A ward brings its own body whatever the violation is made of. */
    const wantsDome = this.instance.arrival === 'dome';
    if (!wantsDome && !this.profile.volume) return;
    /*
     * A line, a cluster and a SINGLE CELL get no body. There is no shape a one-cell
     * hit is; standing a lit cylinder on it puts a silhouette in the scene — two
     * vertical edges and a cap — and a silhouette is the one thing that gives away
     * that an effect is geometry rather than light. What sells a single hit is the
     * flash on the floor and the debris leaving it.
     */
    if (!wantsDome && (hint === 'line' || hint === 'cluster' || hint === 'single')) return;

    const origin = this.params.origin;
    const originComponent = this.topology.components.find(component => component.keys.has(
      `${Math.floor(origin.x)},${Math.floor(origin.z)}`,
    )) || this.topology.components[0];
    if (!originComponent) return;

    const params = this.params;
    let geometry = null;
    let height = 1;
    /*
     * A ward is a held object, not a stain on the floor: whatever cells it covers,
     * the thing itself is a shell around its subject. So it overrides the footprint's
     * usual body and is the one volume drawn as a fresnel shell.
     */
    const dome = this.instance.arrival === 'dome';
    if (dome) {
      /*
       * Taller than it is wide. The shell used to be squatter than its own radius,
       * which on a raked board flattens it into a saucer — and a saucer is read as
       * one wide ellipse rather than as something standing around a figure.
       */
      const radius = Math.max(0.72, Math.min(this.groundReach, params.reach) * 0.8);
      geometry = new SphereGeometry(1, 40, 24, 0, Math.PI * 2, 0, Math.PI / 2);
      height = radius * 1.45;
      geometry.scale(radius, height, radius);
    } else if (hint === 'burst') {
      /*
       * Held inside the ground the effect actually claims. The ideal radius is
       * allowed to run a little past the cells — that is what `clipped-shape` exists
       * to trim — and a dome built on the ideal radius would put a lit body over
       * cells nothing happened on, which is the one thing the merged contour is
       * there to deny.
       */
      const footprintRadius = Math.min(params.radius, this.groundReach) * 0.86;
      geometry = new SphereGeometry(1, 28, 16, 0, Math.PI * 2, 0, Math.PI / 2);
      height = footprintRadius * 0.62;
      geometry.scale(footprintRadius, height, footprintRadius);
    } else if (hint === 'single') {
      geometry = new CylinderGeometry(0.3, 0.42, 1.0, 20, 1, true);
      geometry.translate(0, 0.5, 0);
      height = 1.0;
    } else if (hint === 'ring') {
      geometry = new CylinderGeometry(params.radius, params.radius, 0.8, 48, 1, true);
      geometry.translate(0, 0.4, 0);
      height = 0.8;
    } else if (hint === 'cone') {
      const shape = new Shape();
      shape.moveTo(0, 0);
      const steps = 18;
      for (let i = 0; i <= steps; i++) {
        const angle = -params.halfAngle + (params.halfAngle * 2 * i) / steps;
        shape.lineTo(Math.cos(angle) * params.radius, Math.sin(angle) * params.radius);
      }
      shape.lineTo(0, 0);
      geometry = new ExtrudeGeometry(shape, { depth: 0.6, bevelEnabled: false });
      geometry.rotateX(-Math.PI / 2);
      height = 0.6;
    }
    if (!geometry) return;

    const mesh = new Mesh(geometry, createVolumeMaterial({
      shared: this.uniforms,
      violation: this.violation,
      path: this.path,
      height,
      component: originComponent,
      sdfTexture: this.componentTextures.get(originComponent),
      /* A dome stands over its subject, so it is not trimmed to the floor plan. */
      clip: !dome && mergeMode !== 'ideal-shape',
      dome,
      spectrum: dome ? (this.instance.spectrum || []) : [],
    }));
    mesh.position.set(params.origin.x, 0, params.origin.z);
    if (hint === 'cone') {
      mesh.rotation.y = -Math.atan2(params.direction.z, params.direction.x);
    }
    mesh.renderOrder = RENDER_ORDER.volume;
    mesh.frustumCulled = false;
    this.group.add(mesh);
    this.volume = mesh;
    /*
     * A body is what ARRIVES. A ward is the exception and the reason this is not a
     * blanket rule: the shell IS the thing being placed, so previewing a ward without
     * it previews nothing. Everything else — the dome over a blast, the column of a
     * beam — is the effect happening, and an aim that already shows it has spent the
     * moment the cast exists to deliver.
     */
    if (!dome) this.arrivalOnly.push(mesh);
  }

  /** The worked ring an arranged action leaves under its subject. */
  addSigil() {
    /* Held to the ground the action claimed: a mark wider than its own effect reads
       as a decal the effect was placed on top of. */
    const radius = Math.max(0.75, Math.min(this.groundReach, this.params.reach)) + 0.16;
    const geometry = new CircleGeometry(radius, 64);
    geometry.rotateX(-Math.PI / 2);
    const mesh = new Mesh(geometry, createSigilMaterial({
      shared: this.uniforms,
      violation: this.violation,
      path: this.path,
      intent: this.intent,
      radius,
    }));
    mesh.position.set(this.params.origin.x, 0.015, this.params.origin.z);
    mesh.renderOrder = RENDER_ORDER.sigil;
    mesh.frustumCulled = false;
    this.group.add(mesh);
  }

  /**
   * The ground lit by the arrival.
   *
   * Every effect gets one, not just the ones that used to carry a shock ring: an
   * impact that does not throw light onto the floor it lands on is a picture pasted
   * over the board rather than something that happened on it.
   */
  addFlash() {
    /*
     * How far thrown light may run past the cells it belongs to.
     *
     * It may run past them — a pool that stops exactly on the cell boundary is a
     * stencil, and a stencil is the outline this renderer refuses. But the overrun
     * has to be a FALLOFF, which is a fixed distance, not a fraction of the effect's
     * reach: at `reach * 1.4` a one-cell heal lit almost a cell and a half of floor
     * around a half-cell footprint, and a nine-cell blast lit a fifteen-cell disc.
     * So it is a third of a cell either side, whatever the effect's size.
     */
    const radius = Math.min(this.groundReach, this.params.reach) + 0.34;
    const geometry = new CircleGeometry(radius, 40);
    geometry.rotateX(-Math.PI / 2);
    const mesh = new Mesh(geometry, createFlashMaterial({
      shared: this.uniforms,
      violation: this.violation,
      path: this.path,
    }));
    mesh.position.set(this.params.origin.x, 0.01, this.params.origin.z);
    mesh.renderOrder = RENDER_ORDER.flash;
    mesh.frustumCulled = false;
    this.group.add(mesh);
    this.arrivalOnly.push(mesh);
  }

  addHalo() {
    const geometry = new CircleGeometry(this.params.reach * 1.55, 48);
    geometry.rotateX(-Math.PI / 2);
    const mesh = new Mesh(geometry, createOcclusionMaterial({ shared: this.uniforms }));
    mesh.position.set(this.params.origin.x, 0, this.params.origin.z);
    mesh.renderOrder = RENDER_ORDER.occlusion;
    mesh.frustumCulled = false;
    this.group.add(mesh);
  }

  setPhase(phase) {
    if (this.phase === phase) return;
    this.phase = phase;
    this.phaseElapsed = 0;
    if (phase === 'preview') {
      this.uniforms.uDissolve.value = 0;
      this.uniforms.uChar.value = 0;
      this.uniforms.uBurst.value = 0;
      this.uniforms.uGrowth.value = this.aimGrowth;
    } else if (phase === 'execute') {
      this.uniforms.uBurst.value = 0;
      this.uniforms.uDissolve.value = 0;
      /* Arrival is time zero for everything one-shot: the burst, the ground flash. */
      this.uniforms.uSince.value = 0;
    } else if (phase === 'persist') {
      this.uniforms.uBurst.value = 1;
      this.uniforms.uGrowth.value = 1;
      this.uniforms.uDissolve.value = 0;
    }
    this.applyPhaseVisibility();
  }

  setIntensity(intensity) {
    this.uniforms.uIntensity.value = intensity;
  }

  /**
   * The lifecycle, as four different jobs rather than one fade.
   *
   * preview telegraphs, low and readable, with the exact boundary showing; execute
   * is the impulse and the light; persist is a slow loop for ground that stays
   * changed; expire dissolves through the same noise field the surface is made of,
   * so it retreats the way the effect was shaped rather than dimming uniformly.
   */
  update(dt, now) {
    const seconds = dt / 1000;
    this.uniforms.uTime.value += seconds;
    if (this.phase !== 'preview') this.uniforms.uSince.value += seconds;
    this.phaseElapsed += dt;
    const duration = this.instance.durationMs || PHASE_DURATION_MS[this.phase];
    const uniforms = this.uniforms;
    const approach = (uniform, target, rate) => {
      uniform.value += (target - uniform.value) * Math.min(1, seconds * rate);
    };

    if (this.phase === 'preview') {
      approach(uniforms.uActivation, this.aimActivation, 6);
      uniforms.uBurst.value = Math.max(0, uniforms.uBurst.value - seconds * 2.5);
      approach(uniforms.uGrowth, this.aimGrowth, 4);
      uniforms.uChar.value = Math.max(0, uniforms.uChar.value - seconds * 0.8);
      uniforms.uDissolve.value = Math.max(0, uniforms.uDissolve.value - seconds * 3);
    } else if (this.phase === 'execute') {
      const progress = Math.min(1, this.phaseElapsed / duration);
      approach(uniforms.uActivation, 1.0, 10);
      uniforms.uBurst.value = Math.min(1, this.phaseElapsed / 900);
      uniforms.uGrowth.value = Math.min(1, 0.2 + progress * 1.15);
      uniforms.uChar.value = Math.min(1, uniforms.uChar.value + seconds * 0.85);
    } else if (this.phase === 'persist') {
      approach(uniforms.uActivation, 0.62 + 0.05 * Math.sin(now * 0.004), 5);
      uniforms.uBurst.value = 1;
      uniforms.uGrowth.value = 1;
      uniforms.uChar.value = Math.min(1, uniforms.uChar.value + seconds * 0.12);
    } else if (this.phase === 'expire') {
      const progress = Math.min(1, this.phaseElapsed / duration);
      uniforms.uDissolve.value = progress * progress;
      uniforms.uActivation.value = 0.62 * (1 - progress * 0.9);
      uniforms.uGrowth.value = Math.max(0, 1 - progress * 1.1);
    }

  }

  get phaseProgress() {
    const duration = this.instance.durationMs || PHASE_DURATION_MS[this.phase];
    return Math.min(1, this.phaseElapsed / duration);
  }

  get particleCount() {
    let total = 0;
    this.group.children.forEach(child => {
      total += child.userData.particleCount || 0;
    });
    return total;
  }

  dispose() {
    this.group.children.forEach(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
    this.group.clear();
    this.textures.forEach(texture => texture.dispose());
    this.textures = [];
    this.componentTextures.clear();
  }
}

/**
 * One use of one skill, played from the press to the last ember.
 *
 * The phases are the sentence the action makes: something is thrown or swung or
 * called down, it arrives, the ground is changed, and the change fades. Running it
 * as a small state machine on the stage's own clock — rather than as a chain of
 * timers — means the whole thing pauses with the tab, cannot outlive the board it
 * is drawn on, and can be stepped by a bench that wants to look at one frame of it.
 */
/** How long the attacker stands on top of its target before pulling back. */
const LUNGE_HOLD = 0.3;

/**
 * How far along the lunge a blow is, as a fraction of the ground it closes.
 *
 * Two different curves, because the two halves are not the same motion. Going in is
 * a body committing: cubic ease-out, most of the distance covered in the first third
 * of the commit and the last of it arriving soft, so the swing lands on a figure
 * already there rather than on one still travelling. Coming back is a smoothstep
 * over the whole arrival — a recovery has no urgency, and a body that snaps back
 * reads as the animation being cancelled rather than as the fighter stepping off.
 *
 * Pure, and exported, because "the attacker ends where it started" is a property of
 * this function — it has to reach exactly 0 — and testing it through a WebGL stage
 * would test the GPU instead.
 */
export function lungeAmountFor(state, elapsed, { traceMs = 0, arrivalMs = 0 } = {}) {
  if (state === 'lunge') {
    const progress = traceMs > 0 ? Math.min(1, Math.max(0, elapsed / traceMs)) : 1;
    return 1 - Math.pow(1 - progress, 3);
  }
  if (state === 'impact') return 1;
  if (state === 'settle') {
    const hold = arrivalMs * LUNGE_HOLD;
    if (elapsed <= hold) return 1;
    const span = Math.max(1, arrivalMs - hold);
    const progress = Math.min(1, (elapsed - hold) / span);
    return 1 - progress * progress * (3 - 2 * progress);
  }
  return 0;
}

class RunningCast {
  constructor(cast, stage, hooks = {}) {
    this.cast = cast;
    this.stage = stage;
    this.hooks = hooks;
    this.state = cast.lunge > 0 ? 'lunge' : (cast.trace === 'none' ? 'impact' : 'trace');
    this.elapsed = 0;
    this.trace = null;
    this.slash = null;
    this.arrival = null;
    this.done = false;
    this.lungeAmount = 0;

    const project = (cell) => ({ x: cell.col + 0.5, z: cell.row + 0.5 });
    this.from = cast.caster ? project(cast.caster) : project(cast.target || { col: 0, row: 0 });
    this.to = cast.target ? project(cast.target) : this.from;

    if (cast.casterKick > 0 && cast.caster?.id) {
      impactShake.hit(cast.caster.id, cast.casterKick * 0.6, 260);
    }
    /*
     * The commit is drawn whether or not the host moves anything: a board with no
     * token layer, or one under `prefers-reduced-motion`, still gets the low streak
     * of light and grit thrown down the line the attacker closes along.
     */
    this.startFailed = false;
    try {
      if (this.state === 'lunge') {
        this.startTrace();
      }
      if (this.state === 'trace') this.startTrace();
      /*
       * A no-trace delivery (a ward, a field) strikes immediately, here in the
       * constructor — before `playCast` has anywhere to push `this`. `finish` reads
       * `this.hooks.onFinish`, which is the closure that removes THIS instance from
       * the stage's `running` list and resolves the promise `playCast` returned; that
       * closure closes over the very `const running = new RunningCast(...)` binding
       * still being assigned while this constructor runs, so calling it now would
       * read the binding before it exists. `startFailed` defers the call to the
       * first `advance`, by which point `running` is safely assigned and pushed —
       * so a strike that fails here still reaches `finish` instead of leaving an
       * effect on the board with no running cast left to ever retire it.
       */
      if (this.state === 'impact') this.strike();
    } catch (error) {
      console.warn('[ImpactEffectStage] a cast failed to start; it will finish on its first frame:', error);
      this.startFailed = true;
    }
  }

  startTrace() {
    if (this.stage.suppressTraces) return;
    this.trace = new Trace({
      shared: this.stage.projectionUniforms,
      cast: this.cast,
      from: this.from,
      to: this.to,
      group: this.stage.traceGroup,
    });
  }

  /**
   * Arrival.
   *
   * Everything that makes an impact land happens in this one place and in this one
   * frame: the ground effect starts, the figures standing on the affected cells are
   * shaken, and the swing — if this was a swing — is drawn. Splitting them across
   * frames is what makes a hit feel like three unrelated animations.
   */
  strike() {
    const cast = this.cast;
    this.stage.setEffect({
      id: cast.id,
      violationType: cast.violationType,
      path: cast.path,
      spectrum: cast.spectrum,
      intent: cast.intent,
      cells: cast.cells,
      origin: cast.target || cast.caster,
      direction: this.facingVector(),
      footprintHint: cast.footprintHint,
      arrival: cast.arrival,
      mergeMode: cast.mergeMode,
      intensity: cast.intensity,
      seed: cast.seed,
      size: cast.size,
      durationMs: cast.arrivalMs,
      phase: 'execute',
    });
    this.stage.setPhase('execute', cast.id);

    if (cast.arrival === 'slash') {
      this.slash = createSlash({
        shared: this.stage.projectionUniforms,
        cast,
        at: this.to,
        facing: Math.atan2(this.to.z - this.from.z, this.to.x - this.from.x),
        group: this.stage.traceGroup,
      });
      this.slashElapsed = 0;
    }

    /*
     * A one-cell hit gets no lit body on the board by design, so without this the
     * whole arrival is a gradient on the floor underneath a figure standing above
     * it. The bloom and its debris belong to the journey, not to the ground: they
     * live in the trace group and claim no cell.
     */
    if (cast.arrival === 'flash' && !this.stage.suppressTraces) {
      this.arrival = createArrival({
        shared: this.stage.projectionUniforms,
        cast,
        at: this.to,
        axis: this.facingVector(),
        group: this.stage.traceGroup,
      });
    }

    /*
     * The radial arrivals. Nothing was thrown at these, so there is no line for
     * debris to leave along: a gathering settles into its subject, a displacement
     * re-forms them, and a summoning throws the floor outward under what arrived.
     */
    const RADIAL_ARRIVALS = { infall: 'in', reform: 'in', landing: 'out' };
    if (RADIAL_ARRIVALS[cast.arrival] && !this.stage.suppressTraces) {
      this.arrival = createConverge({
        shared: this.stage.projectionUniforms,
        cast,
        at: this.to,
        group: this.stage.traceGroup,
        mode: RADIAL_ARRIVALS[cast.arrival],
        radius: cast.arrival === 'infall' ? 1.9 : 1.5,
        durationMs: cast.arrivalMs * 0.85,
      });
    }

    impactShake.hit(`cell:${cast.target?.col},${cast.target?.row}`, cast.impactForce, 460);
    /*
     * A caller's hook is a black box this stage does not control. It used to run
     * before the release below, so a host component throwing out of `onImpact` (bad
     * token data, a stale ref, anything) skipped the release and left the state
     * machine one step short forever — the trace never let go, and the exception
     * climbing out of `strike` aborted the whole frame it was called from, taking
     * the render loop's own re-arm with it. Isolated here so a hook's own failure
     * cannot cost the cast its release.
     */
    try {
      this.hooks.onImpact?.(cast);
    } catch (error) {
      console.warn('[ImpactEffectStage] onImpact hook failed; the cast keeps moving:', error);
    }
    /*
     * The trace is released rather than destroyed. Air that something has just gone
     * through is still moving after it has landed, and cutting the beam on the exact
     * frame of impact is what made the old one read as a light being switched off.
     */
    this.trace?.release();
  }

  facingVector() {
    const dx = this.to.x - this.from.x;
    const dz = this.to.z - this.from.z;
    const length = Math.hypot(dx, dz) || 1;
    return { x: dx / length, z: dz / length };
  }

  /**
   * Where the attacker's body is, this frame.
   *
   * Announced as a fraction rather than as a position: the board owns the morph, the
   * zoom and where the two cells actually are on screen, and an effects layer that
   * computed pixels would be computing them from a projection it does not own.
   */
  setLunge(amount) {
    const next = Math.max(0, Math.min(1, amount));
    if (this.lungeAmount === next) return;
    this.lungeAmount = next;
    this.hooks.onLunge?.(this.cast, next);
  }

  /**
   * `advance` can never let a cast die silently.
   *
   * Everything below is one frame of a hand-rolled state machine touching WebGL
   * disposal, a caller's own DOM hooks and this stage's shared maps — any one of
   * which throwing used to escape all the way up through `ImpactEffectStage.update`
   * and abort the board layer's own frame before it reached the line that re-arms
   * `requestAnimationFrame`. The render loop then went silent for good: no more
   * frames, so no more calls to THIS cast's `advance` either, so whatever phase it
   * was in when the exception fired is the phase it stays in — a cast blazing on
   * the board forever, indistinguishable from a phase that legitimately never
   * advances. Catching here keeps one broken cast from taking the whole reel down
   * with it, and still gets it to `finish` rather than leaving it stuck.
   */
  advance(dt) {
    if (this.startFailed) {
      this.finish();
      return;
    }
    try {
      this.step(dt);
    } catch (error) {
      console.warn('[ImpactEffectStage] a cast failed mid-flight; finishing it rather than leaving it stuck:', error);
      this.finish();
    }
  }

  step(dt) {
    this.elapsed += dt;
    if (this.cast.lunge > 0) this.setLunge(lungeAmountFor(this.state, this.elapsed, this.cast));
    /* A released trace plays its afterglow out alongside whatever happens next. */
    if (this.trace?.released && this.trace.advance(dt)) {
      this.trace.dispose();
      this.trace = null;
    }
    if (this.arrival && this.arrival.advance(dt)) {
      this.arrival.dispose();
      this.arrival = null;
    }
    if (this.slash) {
      this.slashElapsed += dt;
      advanceSlash(this.slash, this.slashElapsed, this.cast.arrivalMs * 0.45);
    }
    if (this.state === 'lunge') {
      if (this.trace && !this.trace.released) this.trace.advance(dt);
      if (this.elapsed >= this.cast.traceMs) {
        this.elapsed = 0;
        this.state = 'impact';
        this.strike();
        this.state = 'settle';
      }
      return;
    }
    if (this.state === 'trace') {
      if (!this.trace || this.trace.advance(dt)) {
        this.elapsed = 0;
        this.state = 'settle';
        this.strike();
      }
      return;
    }
    if (this.state === 'impact') {
      this.state = 'settle';
      this.elapsed = 0;
      return;
    }
    if (this.state === 'settle') {
      if (this.slash && this.elapsed > this.cast.arrivalMs * 0.85) {
        this.slash.geometry.dispose();
        this.slash.material.dispose();
        this.stage.traceGroup.remove(this.slash);
        this.slash = null;
      }
      if (this.elapsed >= this.cast.arrivalMs) {
        this.elapsed = 0;
        this.state = 'persist';
        this.stage.setPhase('persist', this.cast.id);
        /* Stated, never merely approached: the settle's last frame may land at any
           dt, and an attacker left a tenth of a cell from home stays there. */
        if (this.cast.lunge > 0) this.setLunge(0);
      }
      return;
    }
    if (this.state === 'persist') {
      if (this.elapsed >= this.cast.persistMs) {
        this.elapsed = 0;
        this.state = 'expire';
        this.stage.setPhase('expire', this.cast.id);
      }
      return;
    }
    if (this.state === 'expire' && this.elapsed >= this.cast.expireMs) {
      this.finish();
    }
  }

  /**
   * The one door out, and it always opens.
   *
   * `onFinish` is what drops this cast from the stage's `running` list and resolves
   * the promise `playCast` handed out — the barrier the next round of the reel, and
   * whoever is awaiting this cast, both wait on. A WebGL dispose or a stage lookup
   * throwing partway through used to skip straight past it, so the promise never
   * settled and `running` never shrank: `hasWork` stayed true and the render loop
   * kept spinning forever on a cast that was, in every way that matters, already
   * dead. Each cleanup step is isolated so the worst any of them can do is leak the
   * one resource it owned — never the exit itself.
   */
  finish() {
    if (this.done) return;
    this.done = true;
    try {
      /* Whatever killed the cast — the reel ending, a board being torn down, a stage
         stopped mid-flight — the body it moved goes back to its own cell. */
      if (this.cast.lunge > 0) this.setLunge(0);
      this.trace?.dispose();
      this.arrival?.dispose();
      this.arrival = null;
      if (this.slash) {
        this.slash.geometry.dispose();
        this.slash.material.dispose();
        this.stage.traceGroup.remove(this.slash);
        this.slash = null;
      }
    } catch (error) {
      console.warn('[ImpactEffectStage] a cast failed to clean up its trace; finishing anyway:', error);
    }
    try {
      this.stage.removeEffect(this.cast.id);
    } catch (error) {
      console.warn('[ImpactEffectStage] a cast failed to remove its ground effect; finishing anyway:', error);
    }
    this.hooks.onFinish?.(this.cast);
  }
}

export class ImpactEffectStage {
  /*
   * `record` keeps the drawing buffer readable after the browser composites the
   * frame. Without it the canvas reads back empty to anything outside the render
   * loop, so a cast can be watched but never captured. It costs a copy per frame,
   * so it is off unless a bench asks for it.
   */
  constructor(canvas, { quality = 'high', record = false, worldProjection = false, camera = null, host = null } = {}) {
    this.canvas = canvas;
    this.quality = quality;
    this.hosted = Boolean(host);
    if (host) {
      this.renderer = host.renderer;
      this.scene = new Group();
      this.scene.name = 'impact-stage';
      camera = host.camera;
      worldProjection = true;
    } else {
      this.renderer = new WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'low-power',
        preserveDrawingBuffer: record,
      });
      this.renderer.setClearColor(0x000000, 0);
      this.scene = new Scene();
    }
    /*
     * A plain Camera with identity matrices. Everything is projected in the vertex
     * shader by the board's own bilinear map — there is no pose a camera could hold
     * that would reproduce it, so it is not asked to.
     */
    this.camera = camera || new Camera();
    if (!camera) this.camera.matrixAutoUpdate = false;

    this.projectionUniforms = {
      uCornerTL: { value: new Vector2() },
      uCornerTR: { value: new Vector2() },
      uCornerBR: { value: new Vector2() },
      uCornerBL: { value: new Vector2() },
      uGridSize: { value: new Vector2(1, 1) },
      uViewport: { value: new Vector2(1, 1) },
      uCellPx: { value: 40 },
    };
    if (worldProjection) this.projectionUniforms[WORLD_PROJECTION] = true;

    this.effects = new Map();
    this.running = [];
    this.traceGroup = new Group();
    this.scene.add(this.traceGroup);
    this.viewport = { width: 0, height: 0 };
    this.warmed = false;
    this.timeScale = 1;
  }

  /**
   * Compile the shaders before anything needs them.
   *
   * A program is linked the first time it is drawn, and these are large: the first
   * cast of a session stalled the main thread long enough to eat most of a second,
   * which on a turn-based board lands exactly on the frame the player is watching
   * for. Every material is the same source whatever the violation — the violation
   * is a uniform, not a define — so one throwaway instance of each kind warms the
   * lot, once, at a moment when nothing is happening.
   *
   * The dummies are drawn to a zero-size scissor rather than merely compiled, so
   * the driver's deferred link work is genuinely done rather than merely queued.
   */
  prewarm() {
    if (this.warmed) return;
    this.warmed = true;

    const component = {
      sdf: { rect: { x: 0, z: 0, width: 1, height: 1 } },
      keys: new Set(['0,0']),
      bounds: { minCol: 0, maxCol: 1, minRow: 0, maxRow: 1 },
      area: 1,
    };
    const params = {
      origin: { x: 0.5, z: 0.5 }, direction: { x: 1, z: 0 },
      radius: 1, halfWidth: 0.5, length: 1, halfAngle: 0.5, reach: 1,
    };
    const shared = {
      ...this.projectionUniforms,
      uTime: { value: 0 }, uActivation: { value: 0 }, uBurst: { value: 0 },
      uDissolve: { value: 0 }, uGrowth: { value: 0 }, uChar: { value: 0 },
      uIntensity: { value: 0 }, uAreaNorm: { value: 1 }, uSeed: { value: 0 },
      uSince: { value: 0 }, uOctaves: { value: 3 },
      uIntent: { value: 0 }, uIntentBias: { value: new Color('#ffffff') },
      uIntentAmount: { value: 0 }, uLift: { value: 0 }, uConverge: { value: 0 },
    };

    const scene = new Scene();
    const geometry = new PlaneGeometry(0.01, 0.01);
    const materials = [
      createDecalMaterial({ shared, component, violation: 'Heat', hint: 'single', mergeMode: 'rounded-cells', params, sdfTexture: null }),
      createVolumeMaterial({ shared, violation: 'Heat', height: 1, component, sdfTexture: null, clip: false }),
      createVolumeMaterial({ shared, violation: 'None', height: 1, component, sdfTexture: null, clip: false, dome: true }),
      createAmbientMaterial({
        shared, violation: 'Heat', hint: 'single', params, motion: ambientMotion('Heat'),
      }),
      createBurstMaterial({ shared, violation: 'Heat' }),
      createSigilMaterial({ shared, violation: 'Heat', intent: INTENTS.mend, radius: 1 }),
      createShardMaterial({ shared, violation: 'Cold', kind: 'crystal' }),
      createFlashMaterial({ shared, violation: 'Heat' }),
      createOcclusionMaterial({ shared }),
    ];
    materials.forEach(material => {
      const mesh = new Mesh(geometry, material);
      mesh.frustumCulled = false;
      scene.add(mesh);
    });

    try {
      this.renderer.compile(scene, this.camera);
    } catch (error) {
      // A warm-up that fails costs a stutter, never the board.
      console.warn('[ImpactEffectStage] shader prewarm skipped:', error);
    }

    materials.forEach(material => material.dispose());
    geometry.dispose();
  }

  /**
   * Play one cast and resolve when it has finished leaving.
   *
   * The hooks are how an effect is allowed to touch the rest of the scene without
   * knowing anything about it: `onLunge` for a body closing on its target,
   * `onImpact` for whatever the board wants to do the moment the ground changes.
   */
  playCast(cast, hooks = {}) {
    const plan = {
      persistMs: 700,
      expireMs: 600,
      ...cast,
    };
    return new Promise(resolve => {
      const running = new RunningCast(plan, this, {
        ...hooks,
        onFinish: (finished) => {
          this.running = this.running.filter(item => item !== running);
          hooks.onFinish?.(finished);
          resolve(finished);
        },
      });
      this.running.push(running);
    });
  }

  /** Stop everything mid-flight, for a bench that wants to start over. */
  stopCasts() {
    [...this.running].forEach(running => running.finish());
    this.running = [];
  }

  setQuality(quality) {
    if (this.quality === quality) return;
    this.quality = quality;
    const instances = [...this.effects.values()].map(effect => effect.instance);
    this.clear();
    instances.forEach(instance => this.setEffect(instance));
    this.resize(this.viewport.width, this.viewport.height);
  }

  resize(width, height) {
    this.viewport = { width, height };
    if (width > 0 && height > 0) this.prewarm();
    const profile = QUALITY_PROFILES[this.quality] || QUALITY_PROFILES.high;
    if (!this.hosted) {
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, profile.dpr));
      this.renderer.setSize(width, height, false);
    }
    this.projectionUniforms.uViewport.value.set(width, height);
  }

  /** Re-point the layer at the board. Called on every pan, zoom and resize. */
  setProjection(projection) {
    if (!projection || !projection.config) return;
    const corners = projection.corners();
    this.projectionUniforms.uCornerTL.value.set(corners.topLeft.x, corners.topLeft.y);
    this.projectionUniforms.uCornerTR.value.set(corners.topRight.x, corners.topRight.y);
    this.projectionUniforms.uCornerBR.value.set(corners.bottomRight.x, corners.bottomRight.y);
    this.projectionUniforms.uCornerBL.value.set(corners.bottomLeft.x, corners.bottomLeft.y);
    this.projectionUniforms.uGridSize.value.set(projection.columns, projection.rows);
    const cell = projection.cellSizePx();
    this.projectionUniforms.uCellPx.value = (cell.width + cell.height) / 2;
  }

  setEffect(instance) {
    if (this.suppressEffects) return null;
    const existing = this.effects.get(instance.id);
    if (existing) {
      this.scene.remove(existing.group);
      existing.dispose();
    }
    const effect = new ImpactEffect(instance, {
      projectionUniforms: this.projectionUniforms,
      quality: this.quality,
    });
    this.effects.set(instance.id, effect);
    this.scene.add(effect.group);
    return effect;
  }

  getEffect(id) {
    return this.effects.get(id) || null;
  }

  removeEffect(id) {
    const effect = this.effects.get(id);
    if (!effect) return;
    this.scene.remove(effect.group);
    effect.dispose();
    this.effects.delete(id);
  }

  clear() {
    [...this.effects.keys()].forEach(id => this.removeEffect(id));
  }

  setPhase(phase, id = null) {
    this.effects.forEach(effect => {
      if (!id || effect.instance.id === id) effect.setPhase(phase);
    });
  }

  /**
   * Slow the whole layer down without touching the host's clock.
   *
   * An impact is over in well under two seconds and a screenshot round-trip is not,
   * so there is no way to LOOK at a frame of it at speed. Everything downstream is
   * driven by `dt` alone, so one multiplier here plays the entire lifecycle — traces,
   * bursts, phases — in slow motion, and nothing else in the stage needs to know.
   */
  setTimeScale(scale) {
    this.timeScale = Math.max(0.02, scale);
  }

  /**
   * One effect or one running cast failing to update must never cost every OTHER
   * effect its frame. `render` and the board layer's own re-arm of `requestAnimationFrame`
   * both run after this returns, so a bare `forEach` here — one throw anywhere in it
   * — used to skip the render and silently kill the loop for good, not just for the
   * effect that broke. `RunningCast.advance` isolates its own failures already; the
   * per-effect uniform update is isolated here for the same reason.
   */
  update(dt, now) {
    const scaled = dt * (this.timeScale ?? 1);
    this.effects.forEach(effect => {
      try {
        effect.update(scaled, now);
      } catch (error) {
        console.warn('[ImpactEffectStage] an effect failed to update:', error);
      }
    });
    [...this.running].forEach(running => running.advance(scaled));
  }

  render() {
    if (this.hosted) return;
    this.renderer.render(this.scene, this.camera);
  }

  stats() {
    const info = this.renderer.info.render;
    let particles = 0;
    let components = 0;
    this.effects.forEach(effect => {
      particles += effect.particleCount;
      components += effect.topology.components.length;
    });
    return {
      calls: info.calls,
      triangles: info.triangles,
      particles,
      components,
      effects: this.effects.size,
    };
  }

  dispose() {
    this.stopCasts();
    this.clear();
    if (!this.hosted) this.renderer.dispose();
  }
}

export default ImpactEffectStage;
